import { useState, useRef } from 'react';
import { 
  Briefcase, 
  Send, 
  Loader2, 
  Phone, 
  Mail, 
  Upload,
  FileText,
  Check
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { SITE_INFO } from '../data';

export default function JobsPage() {
  // Form state
  const formRef = useRef(null);
  const applicationSectionRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileNames, setFileNames] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position: 'Tømrersvend',
    experience_years: '',
    message: '',
    website_url: '' // Anti-spam honeypot (Rule 12)
  });

  const [errors, setErrors] = useState({});

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    // Limit to 5 files, 10MB each, total 20MB (Rule 12)
    let totalSize = 0;
    const validNames = [];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage(`Filen "${file.name}" overskrider grænsen på 10 MB.`);
        return;
      }
      totalSize += file.size;
      validNames.push(file.name);
    }

    if (totalSize > 20 * 1024 * 1024) {
      setErrorMessage('De samlede filer overskrider grænsen på 20 MB.');
      return;
    }

    setErrorMessage('');
    setFileNames(validNames);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Indtast venligst dit fulde navn';
    if (!formData.phone.trim()) newErrors.phone = 'Indtast venligst dit telefonnummer';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Indtast en gyldig e-mailadresse';
    if (!formData.message.trim()) newErrors.message = 'Skriv venligst et par ord om dig selv og din erfaring';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validate()) return;

    setLoading(true);

    try {
      // Must submit as multipart/form-data via FormData for file attachments
      const data = new FormData(formRef.current);

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data,
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          position: jobs[0]?.title || 'Erfaren Tømrersvend til kvalitetsbyggeri',
          experience_years: '',
          message: '',
          website_url: ''
        });
        setFileNames([]);
        if (formRef.current) formRef.current.reset();
      } else {
        const resJson = await response.json().catch(() => ({}));
        setErrorMessage(resJson.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Netværksfejl. Kontroller venligst din internetforbindelse og prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead 
        title={`Ledige Stillinger & Karriere – ${SITE_INFO.name}`}
        description="Søg job hos G28 ApS i Låsby. Vi søger dygtige tømrersvende og lærlinge, der sætter en ære i kompromisløst kvalitetshåndværk."
      />

      {/* Hero Header */}
      <section className="bg-stone-900 text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-900/60 to-transparent z-10" />
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/image00016.jpeg"
          alt="G28 byggeplads med firmabiler og stillads"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-30"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-20">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wider">
              <Briefcase className="w-3.5 h-3.5" aria-hidden="true" />
              Karriere hos G28 ApS
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1]">
              BLIV EN DEL AF HOLDET
            </h1>
            <p className="text-stone-300 text-base sm:text-lg lg:text-xl leading-relaxed">
              Vi skaber særlige boliger og kompromisløst håndværk for kvalitetsbevidste kunder. Vil du have et arbejde, hvor faglig stolthed, ordentlighed og godt kollegaskab er i højsædet?
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#ansoegning"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-amber-500 text-stone-950 font-heading font-bold text-sm uppercase tracking-wider hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/10"
              >
                Send ansøgning
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section 
        id="ansoegning" 
        ref={applicationSectionRef} 
        className="py-20 bg-stone-50 text-stone-900 border-b border-stone-200 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Contact Info & Reassurance */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-widest text-amber-900 block">
                  Send din ansøgning
                </span>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight">
                  VI GLÆDER OS TIL AT HØRE FRA DIG
                </h2>
              </div>

              <p className="text-stone-700 text-base leading-relaxed">
                Udfyld formularen her, så vender vi hurtigt tilbage til dig. Vi holder løbende samtaler og ansætter, så snart vi finder den rette profil.
              </p>

              <div className="bg-white p-6 border border-stone-200 space-y-4 shadow-sm">
                <h3 className="font-heading font-bold text-stone-900 text-base uppercase tracking-wider">
                  Vil du hellere ringe for en snak?
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Du er altid velkommen til at kontakte Simon direkte for en uforpligtende og fortrolig dialog om mulighederne hos G28.
                </p>
                <div className="space-y-3 pt-2">
                  <a 
                    href={`tel:${SITE_INFO.phone}`}
                    className="flex items-center gap-3 text-stone-900 hover:text-amber-800 font-semibold text-base transition-colors"
                  >
                    <div className="w-10 h-10 bg-amber-500/10 flex items-center justify-center text-amber-800">
                      <Phone className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <span>{SITE_INFO.phoneDisplay}</span>
                  </a>
                  <a 
                    href={`mailto:${SITE_INFO.email}?subject=Ansøgning%20til%20G28`}
                    className="flex items-center gap-3 text-stone-900 hover:text-amber-800 font-semibold text-base transition-colors"
                  >
                    <div className="w-10 h-10 bg-amber-500/10 flex items-center justify-center text-amber-800">
                      <Mail className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <span>{SITE_INFO.email}</span>
                  </a>
                </div>
              </div>

              <div className="p-4 bg-stone-100 border border-stone-200 text-xs text-stone-600 space-y-1">
                <p className="font-bold text-stone-800">Fortrolighed & datasikkerhed</p>
                <p>Alle henvendelser og ansøgninger behandles naturligvis 100% fortroligt og slettes, når rekrutteringsprocessen er afsluttet.</p>
              </div>
            </div>

            {/* Right: Application Form */}
            <div className="lg:col-span-7 bg-white p-8 sm:p-10 border border-stone-200 shadow-md">
              <h3 className="font-heading font-bold text-xl text-stone-900 mb-6">
                Ansøgningsformular
              </h3>

              {success ? (
                <div className="p-8 bg-emerald-50 border border-emerald-200 text-emerald-950 space-y-4 text-center">
                  <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto">
                    <Check className="w-8 h-8" aria-hidden="true" />
                  </div>
                  <h4 className="font-heading font-extrabold text-2xl text-emerald-900">
                    Tak for din ansøgning!
                  </h4>
                  <p className="text-emerald-800 text-sm leading-relaxed max-w-md mx-auto">
                    Vi har modtaget dine oplysninger og vender tilbage til dig hurtigst muligt. Du er også velkommen til at kontakte os direkte på {SITE_INFO.phoneDisplay}.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-4 px-6 py-2.5 bg-emerald-800 text-white font-heading font-bold text-xs uppercase tracking-wider hover:bg-emerald-700 transition-colors cursor-pointer"
                  >
                    Send endnu en henvendelse
                  </button>
                </div>
              ) : (
                <form 
                  ref={formRef} 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  noValidate
                >
                  {/* Anti-spam Honeypot (Mandatory Rule 12) */}
                  <input
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    className="sr-only absolute"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  {/* Error banner */}
                  {errorMessage && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                      {errorMessage}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="position" className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                      Hvilken stilling søger du? *
                    </label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:outline-none focus:border-amber-700 focus:bg-white transition-colors"
                    >
                      <option value="Tømrersvend">Tømrersvend</option>
                      <option value="Tømrerlærling">Tømrerlærling</option>
                      <option value="Uopfordret ansøgning – Tømrer eller Snedker">Uopfordret ansøgning – Tømrer eller Snedker</option>
                      <option value="Andet / Byggeledelse">Andet / Byggeledelse</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                        Dit fulde navn *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="F.eks. Peter Hansen"
                        className={`w-full px-4 py-3 bg-stone-50 border text-stone-900 text-sm focus:outline-none focus:border-amber-700 focus:bg-white transition-colors ${
                          errors.name ? 'border-red-500 bg-red-50/20' : 'border-stone-300'
                        }`}
                      />
                      {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="F.eks. 20 12 34 56"
                        className={`w-full px-4 py-3 bg-stone-50 border text-stone-900 text-sm focus:outline-none focus:border-amber-700 focus:bg-white transition-colors ${
                          errors.phone ? 'border-red-500 bg-red-50/20' : 'border-stone-300'
                        }`}
                      />
                      {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                        E-mailadresse *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="F.eks. peter@eksempel.dk"
                        className={`w-full px-4 py-3 bg-stone-50 border text-stone-900 text-sm focus:outline-none focus:border-amber-700 focus:bg-white transition-colors ${
                          errors.email ? 'border-red-500 bg-red-50/20' : 'border-stone-300'
                        }`}
                      />
                      {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="experience_years" className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                        Års erfaring i faget
                      </label>
                      <input
                        type="text"
                        id="experience_years"
                        name="experience_years"
                        value={formData.experience_years}
                        onChange={handleChange}
                        placeholder="F.eks. 5 år som tømrersvend"
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:outline-none focus:border-amber-700 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                      Lidt om dig selv og dine erfaringer *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Fortæl kort om hvad du har lavet, hvad du er stærkest til, og hvorfor du gerne vil arbejde hos G28..."
                      className={`w-full px-4 py-3 bg-stone-50 border text-stone-900 text-sm focus:outline-none focus:border-amber-700 focus:bg-white transition-colors ${
                        errors.message ? 'border-red-500 bg-red-50/20' : 'border-stone-300'
                      }`}
                    ></textarea>
                    {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
                  </div>

                  {/* File Upload (CV / Svendebrev / Anbefaling) - Rule 12 compliant */}
                  <div className="space-y-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                      Vedhæft CV eller svendebrev (valgfrit)
                    </label>
                    <div className="p-4 border-2 border-dashed border-stone-300 hover:border-amber-700 bg-stone-50 transition-colors text-center cursor-pointer relative">
                      <input
                        type="file"
                        name="cv_fil"
                        id="cv_fil"
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
                        <Upload className="w-6 h-6 text-stone-500" aria-hidden="true" />
                        <span className="text-xs font-semibold text-stone-700">
                          Klik for at vælge filer eller træk dem herover
                        </span>
                        <span className="text-[11px] text-stone-500">
                          PDF, Word, JPG eller PNG (Maks. 5 filer, 10 MB per fil)
                        </span>
                      </div>
                    </div>

                    {fileNames.length > 0 && (
                      <div className="pt-2 space-y-1.5">
                        <span className="text-xs font-bold text-stone-700">Valgte filer:</span>
                        {fileNames.map((name, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-stone-600 bg-stone-100 px-3 py-1.5 border border-stone-200">
                            <FileText className="w-3.5 h-3.5 text-amber-700 shrink-0" aria-hidden="true" />
                            <span className="truncate">{name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-amber-500 hover:bg-amber-400 text-stone-950 font-heading font-extrabold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer disabled:opacity-50 shadow-md"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                        <span>Sender ansøgning...</span>
                      </>
                    ) : (
                      <>
                        <span>Send ansøgning</span>
                        <Send className="w-4 h-4" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
